import { Error } from "@carbon/icons-react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import PageLoader from "../../components/PageLoader";
import CtaSection from "../../components/deliveryCtaSection/DeliveryCtaSection";
import DetailsGeneralInfo from "../../components/deliveryGeneralInfo/DeliveryGeneralInfo";
import FallbackComponent from "../../components/fallbackComponent/FallbackComponent";
import OrderHeader from "../../components/orderHeader/OrderHeader";
import { useHeader } from "../../contexts/PageHeaderContext";
import useInboundDeliveryByIdQuery from "../../hooks/graphql-hooks/queries/useInboundDeliveryByIdQuery";
import { InboundDeliveryFragment } from "../../models/autogenerated-deliveryService-gql/graphql";
import DetailsTabCard from "./components/detailsTabCard/DetailsTabCard";

interface DeliveryDetailsProps {}

const DeliveryDetails = (props: DeliveryDetailsProps) => {
  const { t } = useTranslation();

  const { id: idFromUrl } = useParams();
  const { updateHeader, updateSecondHeader } = useHeader();
  const [inboundDelivery, setInboundDelivery] =
    useState<InboundDeliveryFragment>();

  const { loading, error } = useInboundDeliveryByIdQuery({
    id: idFromUrl,
    dataAction: (inboundDelivery) => {
      setInboundDelivery(inboundDelivery);
      updateHeaders(inboundDelivery);
    },
    errorAction: () => {
      updateHeader(<>{t("delivery")}</>);
    },
  });

  const updateHeaders = (inboundDelivery: InboundDeliveryFragment) => {
    const { deliveryNumber } = inboundDelivery;

    updateHeader(<>{t("deliveryDetails")}</>);
    updateSecondHeader(<> {`${t("deliveryNumber")}: ${deliveryNumber}`}</>);
  };

  if (error && !inboundDelivery)
    return (
      <FallbackComponent
        middleIcon={<Error size={32} />}
        text={t("deliveryDetailsError")}
      />
    );

  if (loading || !inboundDelivery) return <PageLoader />;

  return (
    <div className="page-content">
      <Helmet title={t("deliveryDetails")} />
      <div className="page-content-header">
        <div className="mt-10">
          <OrderHeader
            status={inboundDelivery.status}
            amount={inboundDelivery.numberOfItems}
          />
        </div>
        <DetailsGeneralInfo
          delivery={inboundDelivery}
          showActions={true}
          showLocation={true}
        />
        <CtaSection
          actions={inboundDelivery.actions}
          deliveryId={inboundDelivery.id}
        />
        <div className="mt-15">
          <DetailsTabCard delivery={inboundDelivery} />
        </div>
      </div>
    </div>
  );
};

export default DeliveryDetails;
